import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { manchiApi } from "@/lib/manchiApi";
import { Button } from "@/components/ui/button";
import { Loader2, FileText, Copy, Linkedin, Trash2, CheckCircle2, Send, RefreshCw, BookOpen, X } from "lucide-react";
import { toast } from "sonner";
import ActivityHeatmap from "@/pages/manchi/ActivityHeatmap";

const STATUS_STYLE = {
  draft: "bg-pink-100 text-rose-500",
  approved: "bg-amber-100 text-amber-600",
  posted: "bg-emerald-100 text-emerald-600",
};

export default function ManchiDrafts() {
  const location = useLocation();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(!!location.state?.scheduling);
  const pollRef = useRef(null);
  const baselineRef = useRef(null);
  const [activity, setActivity] = useState(null);
  const [substack, setSubstack] = useState(null);

  const load = async (silent) => {
    if (!silent) setLoading(true);
    try { const { data } = await manchiApi.get("/articles"); setArticles(data || []); return data || []; }
    catch { toast.error("Couldn't load drafts"); return []; }
    finally { if (!silent) setLoading(false); }
  };

  useEffect(() => {
    load().then((list) => { baselineRef.current = list.length; });
    manchiApi.get("/activity?days=365").then(({ data }) => setActivity(data)).catch(() => {});
    manchiApi.get("/substack/status").then(({ data }) => setSubstack(data)).catch(() => {});
    if (location.state?.scheduling) {
      let ticks = 0;
      pollRef.current = setInterval(async () => {
        ticks += 1;
        const list = await load(true);
        const base = baselineRef.current ?? 0;
        if (list.length - base >= 5 || ticks >= 14) {
          clearInterval(pollRef.current);
          setBuilding(false);
        }
      }, 10000);
    }
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (a, status) => {
    try {
      await manchiApi.patch(`/articles/${a.id}`, { status });
      setArticles((list) => list.map((x) => (x.id === a.id ? { ...x, status } : x)));
      toast.success(`Marked ${status}`);
    } catch { toast.error("Couldn't update"); }
  };

  const remove = async (a) => {
    try { await manchiApi.delete(`/articles/${a.id}`); setArticles((l) => l.filter((x) => x.id !== a.id)); toast.success("Deleted"); }
    catch { toast.error("Couldn't delete"); }
  };

  const copyOpen = async (a, open) => {
    const text = `${a.title}\n\n${a.body}\n\n${(a.hashtags || []).join(" ")}`;
    try { await navigator.clipboard.writeText(text); toast.success("Copied 📋"); } catch {}
    if (open) window.open("https://www.linkedin.com/feed/?shareActive=true", "_blank", "noopener");
  };

  const publishSubstack = async (a) => {
    const text = `${a.title}\n\n${a.body}`;
    try { await navigator.clipboard.writeText(text); toast.success("Copied — paste into Substack"); } catch {}
    window.open(substack?.publish_url || "https://substack.com", "_blank", "noopener");
  };

  const dismissSubstack = async () => {
    try { await manchiApi.post("/substack/dismiss-reminder"); setSubstack((s) => ({ ...s, due: false })); }
    catch {}
  };

  return (
    <div className="space-y-6" data-testid="manchi-drafts">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-rose-400 flex items-center gap-2">
            <FileText className="w-4 h-4" /> your library
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-rose-900 mt-1">Drafts &amp; posts.</h1>
        </div>
        <button onClick={() => load()} data-testid="manchi-drafts-refresh"
          className="text-xs font-semibold text-rose-500 hover:text-rose-700 flex items-center gap-1.5">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {building && (
        <div data-testid="manchi-building-banner" className="rounded-2xl bg-gradient-to-r from-pink-100 to-purple-100 border border-pink-200 px-4 py-3 flex items-center gap-2.5 text-rose-600 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Building your themed week — drafts are popping in as they finish…
        </div>
      )}

      {substack?.due && (
        <div data-testid="manchi-substack-banner" className="rounded-2xl bg-gradient-to-r from-orange-100 to-pink-100 border border-pink-200 px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 text-rose-700 text-sm">
            <BookOpen className="w-4 h-4 text-pink-500" />
            <span>Time for a new <b>Substack</b> post — it&apos;s been a while. Publish one to keep your readers warm.</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => window.open(substack?.publish_url || "https://substack.com", "_blank", "noopener")}
              data-testid="manchi-substack-write" className="rounded-xl bg-pink-500 hover:bg-pink-600 text-white text-xs h-8">
              Write now
            </Button>
            <button onClick={dismissSubstack} data-testid="manchi-substack-dismiss" className="text-rose-400 hover:text-rose-600 p-1"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {activity && <ActivityHeatmap data={activity} />}

      {loading ? (
        <div className="text-center py-16 text-rose-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : articles.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-pink-200 p-12 text-center text-rose-400">
          Nothing here yet. Head to <Link to="/manchi/studio" className="text-pink-600 underline">Compose</Link> to write your first post.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {articles.map((a) => (
            <div key={a.id} data-testid={`manchi-draft-${a.id}`} className="bg-white rounded-3xl border border-pink-100 shadow-lg shadow-pink-200/30 p-5 flex flex-col">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full ${STATUS_STYLE[a.status] || STATUS_STYLE.draft}`}>{a.status}</span>
                  {a.theme && <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-purple-100 text-purple-500">{a.theme}</span>}
                </div>
                <span className="text-[11px] text-rose-300">{a.scheduled_for ? `📅 ${a.scheduled_for}` : (a.created_at || "").slice(0, 10)}</span>
              </div>
              <h3 className="font-bold text-rose-900 leading-snug">{a.title}</h3>
              <p className="text-rose-500 text-sm mt-1.5 line-clamp-4 whitespace-pre-line flex-1">{a.body}</p>
              <div className="text-pink-500 text-xs font-medium mt-2 line-clamp-1">{(a.hashtags || []).join(" ")}</div>

              <div className="flex flex-wrap gap-1.5 mt-4">
                <Button size="sm" onClick={() => copyOpen(a, true)} data-testid={`manchi-draft-linkedin-${a.id}`}
                  className="rounded-xl bg-[#0a66c2] hover:bg-[#004182] text-white text-xs h-8">
                  <Linkedin className="w-3.5 h-3.5 mr-1" /> Post
                </Button>
                <Button size="sm" variant="outline" onClick={() => copyOpen(a, false)}
                  className="rounded-xl border-pink-200 text-rose-600 hover:bg-pink-50 text-xs h-8">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => publishSubstack(a)} data-testid={`manchi-draft-substack-${a.id}`}
                  className="rounded-xl border-orange-200 text-orange-600 hover:bg-orange-50 text-xs h-8">
                  <BookOpen className="w-3.5 h-3.5 mr-1" /> Substack
                </Button>
                {a.status !== "approved" && (
                  <Button size="sm" variant="outline" onClick={() => setStatus(a, "approved")} data-testid={`manchi-draft-approve-${a.id}`}
                    className="rounded-xl border-amber-200 text-amber-600 hover:bg-amber-50 text-xs h-8">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                )}
                {a.status !== "posted" && (
                  <Button size="sm" variant="outline" onClick={() => setStatus(a, "posted")} data-testid={`manchi-draft-posted-${a.id}`}
                    className="rounded-xl border-emerald-200 text-emerald-600 hover:bg-emerald-50 text-xs h-8">
                    <Send className="w-3.5 h-3.5 mr-1" /> Posted
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => remove(a)} data-testid={`manchi-draft-delete-${a.id}`}
                  className="rounded-xl border-pink-200 text-rose-400 hover:bg-rose-50 hover:text-rose-600 text-xs h-8 ml-auto">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
